import React, { useState, useEffect } from "react"
import { Box, Hidden, Grid } from "@mui/material"
import { makeStyles } from "makeStyles"
import { EventData } from "xstate"
import { useMachine } from "@xstate/react"
import qs from "qs"

import { Phone } from "components/i18n/Contact"

import productAdvisorMachine, {
	ProductAdvisorMachineState,
	ProductAdvisorMachineEvent,
	ProductAdvisorContext,
	Question as QuestionI,
	Answer as AnswerI,
	AnswerSearchState,
} from "@secureo/common/machines/productAdvisorMachine"

import questionsByQuestionId from "config/advisor"
import wait from "@secureo/common/utils/wait"
import { CustomAppProps } from "pages/_app"
import Button from "components/Buttons/Button"

import { BsArrowLeft } from "react-icons/bs"

import Answer from "containers/Advisor/Answer"
import { CountryCode } from "@secureo/common/typings/CountryCode"
import { GetServerSideProps } from "next"
import { anonymousAccessTokenCache } from "@secureo/common/utils/commerceTools/token/accessTokenCache"
import fetchCategoryIdsBySlug from "utils/fetchCategoryIdsBySlug"
import Spinner from "components/Spinner"
import SEO from "components/SEO"

const useStyles = makeStyles()(() => ({
	answerGrid: {
		display: "flex",
		alignItems: "center",
		flexWrap: "wrap",
		"@media only screen and (max-width: 960px)": {
			display: "block",
		},
		"@media only screen and (max-width: 600px)": {
			display: "block",
		},
	},
	responsivePadding: {
		padding: "5vh",
		"@media only screen and (max-width: 960px)": {
			padding: "1.5vh",
		},
	},
}))

const Question = ({
	question,
	questionId,
	selectAnswer,
	countryCode,
	categoryIdsBySlug,
	selectedAnswers,
}: {
	question: QuestionI
	questionId: string
	selectAnswer: (answer: AnswerI) => void
	countryCode: CountryCode
	categoryIdsBySlug: { [slug: string]: string }
	selectedAnswers: ProductAdvisorContext["selectedAnswers"]
}) => {
	const [description, setDescription] = useState("")
	const { classes } = useStyles()
	const { label, answers } = question

	return (
		<Box mt={3}>
			<h2 style={{ fontSize: 20 }}>{label}</h2>
			<div className={classes.answerGrid}>
				{answers.map((answer, i) => {
					return (
						<Answer
							key={`${questionId}-${i}`}
							answer={answer}
							selectAnswer={(answer: AnswerI) => {
								setDescription("")
								selectAnswer(answer)
							}}
							setDescription={setDescription}
							questionId={questionId}
							countryCode={countryCode}
							categoryIdsBySlug={categoryIdsBySlug}
							selectedAnswers={selectedAnswers}
						/>
					)
				})}
			</div>
			<div style={{ minHeight: 40 }}>
				{description && (
					<p
						style={{
							textAlign: "center",
							border: "1px solid rgba(1, 178, 196, 0.4)",
							padding: 5,
							borderRadius: 10,
							boxShadow: "0 0px 8px rgba(0, 0, 0, 0.07)",
							maxWidth: 730,
						}}
					>
						{description}
					</p>
				)}
			</div>
		</Box>
	)
}

export const getSearchStateFromSelectedAnswers = (
	selectedAnswers: ProductAdvisorContext["selectedAnswers"],
	countryCode: CountryCode,
	categoryIdsBySlug: { [slug: string]: string },
) => {
	const searchState = selectedAnswers.reduce(
		(searchState, { answer }) => {
			const answerSearchState = answer?.searchState ?? {
				refinementList: {},
				menu: null,
				sortBy: null,
				range: {},
			}

			const refinementList = Object.entries(answerSearchState?.refinementList ?? {}).reduce(
				(refinementList, [facet, facetValues]) => {
					const prevFacetValues = refinementList[facet] ?? []

					return {
						...refinementList,
						[facet]: [...prevFacetValues, ...facetValues],
					}
				},
				searchState.refinementList,
			)
			const range = {
				...searchState.range,
				...answerSearchState.range,
			}
			const sortBy = answerSearchState?.sortBy ?? searchState.sortBy
			const menu = answerSearchState?.menu ?? searchState.menu

			return {
				...searchState,
				refinementList,
				menu,
				sortBy,
				range,
			}
		},
		{
			refinementList: {},
			menu: {},
			range: {},
			sortBy: null,
		} as AnswerSearchState,
	)

	if (searchState.sortBy) searchState.sortBy = `TRE-de-${countryCode}-${searchState.sortBy}`

	const categoryIdFilters = searchState?.refinementList?.categoryIds ?? []

	if (categoryIdFilters.length > 0) {
		const filteredCategorySlugs = categoryIdFilters.filter((slug) => !!categoryIdsBySlug[slug])
		const categoryIds = filteredCategorySlugs.map((slug) => categoryIdsBySlug[slug])
		searchState.refinementList.categoryIds = categoryIds
	}

	return searchState
}

const ProductAdvisorPage = ({ countryCode, categoryIdsBySlug }: Props & CustomAppProps) => {
	const { classes } = useStyles()
	const [current, send, service] = useMachine(productAdvisorMachine)
	const { currentQuestionId, selectedAnswers } = current.context as ProductAdvisorContext
	const state = current.value as ProductAdvisorMachineState

	const sendEvent = (event: ProductAdvisorMachineEvent, payload?: EventData) => {
		send(event, payload)
	}

	const selectAnswer = async (answer: AnswerI) => {
		sendEvent("SELECT_ANSWER", { answer })

		await wait(200)
		sendEvent("SHOW_ANSWERS")
	}

	const back = () => {
		sendEvent("BACK")
	}

	const currentQuestion = questionsByQuestionId?.[currentQuestionId] ?? null
	const showQuestions = state !== "showResults" && !!currentQuestion

	const aggregatedSearchState = getSearchStateFromSelectedAnswers(
		selectedAnswers,
		countryCode,
		categoryIdsBySlug,
	)

	const hasSelectedAnswers = selectedAnswers.length > 0

	useEffect(() => {
		if (!currentQuestion) {
			window.location.href = `/search?${qs.stringify(aggregatedSearchState)}`
		}
	}, [currentQuestion])

	return (
		<>
			<SEO
				title="Für Ihren Zweck den perfekten Safe finden"
				asPath="/tresorberater"
				description="Sie wissen noch nicht welcher Tresor der richtige für Sie ist? Mit unserem Tresorberater finden Sie garantiert das passende Modell!"
				countryCode={countryCode}
				appendLocalizedShopNameWithPipe
			/>
			<div>
				<Grid container spacing={0}>
					<Hidden mdDown>
						<Grid item xs={12} md={3}>
							<Box
								style={{
									background: "white",
									display: "flex",
									justifyContent: "space-between",
									flexDirection: "column",
									height: "100%",
								}}
							>
								<Box px={7} pt={3} style={{ justifyContent: "normal" }}>
									<h1 style={{ fontSize: 24 }}>
										Was für einen Tresor brauche ich?
									</h1>
									<p>
										Beantworten Sie mit ein paar einfachen Klicks unsere
										Tresorberater Fragen und wir helfen Ihnen garantiert das
										passende Modell zu finden.
									</p>
								</Box>
								<img
									alt="tresorberater"
									src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1616419623/Tresoro%20Neu/ContentGraphics/tresorberater.svg"
									style={{
										width: "100%",
										maxWidth: 250,
										alignSelf: "center",
										paddingBottom: 30,
									}}
								/>
							</Box>
						</Grid>
					</Hidden>
					<Grid item xs={12} md={9}>
						<Box
							style={{
								background: "#F4F4F8",
								display: "flex",
								justifyContent: "space-between",
								flexDirection: "column",
								minHeight: "65vh",
							}}
						>
							<Box
								className={classes.responsivePadding}
								style={{ justifyContent: "normal" }}
							>
								<Box mb={5} style={{ display: "flex" }}>
									<strong style={{ marginRight: 5 }}>
										Brauchen Sie Hilfe? |
									</strong>
									<Phone countryCode={countryCode} />
								</Box>
								{showQuestions ? (
									<Question
										question={currentQuestion}
										questionId={currentQuestionId}
										selectAnswer={selectAnswer}
										selectedAnswers={selectedAnswers}
										countryCode={countryCode}
										categoryIdsBySlug={categoryIdsBySlug}
									/>
								) : (
									<div>
										<h2>Ergebnisse werden geladen</h2>
										<Spinner />
									</div>
								)}
							</Box>
							<Box px={5} pb={2} style={{ background: "#F4F4F8" }}>
								<Box
									style={{
										display: "flex",
										justifyContent: "space-between",
										maxWidth: 950,
									}}
								>
									<Button
										color="secondary"
										onClick={back}
										disabled={!hasSelectedAnswers}
										style={{ color: "white" }}
									>
										<BsArrowLeft size="1.2rem" style={{ marginRight: 5 }} />
										Zurück
									</Button>
									<Button
										color="primary"
										onClick={() => {
											window.location.href = `/search?${qs.stringify(
												aggregatedSearchState,
											)}`
										}}
									>
										Produkte anzeigen
									</Button>
								</Box>
							</Box>
						</Box>
						{/* <pre>{JSON.stringify(aggregatedSearchState, null, 2)}</pre> */}
					</Grid>
				</Grid>
			</div>
		</>
	)
}

interface Props {
	categoryIdsBySlug: { [slug: string]: string }
}

export const getServerSideProps: GetServerSideProps = async () => {
	const accessToken = await anonymousAccessTokenCache()

	const categoryIdsBySlug = await fetchCategoryIdsBySlug(accessToken)

	const props: Props = {
		categoryIdsBySlug,
	}

	return {
		props,
	}
}

export default ProductAdvisorPage
